import { completionEvent } from "../lib/completion-analytics";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { BottomSheet } from "./BottomSheet";
import { TurnstileChallenge } from "./TurnstileChallenge";
import { PhotoAttach } from "./PhotoAttach";
import { contributionRequest, TIP_TAGS } from "../lib/contribution-client";
import { enqueue } from "../lib/outbox";
export function TipComposer({
	seatKey,
	onClose,
	onPublished,
}: {
	seatKey: string;
	onClose: () => void;
	onPublished: () => void;
}) {
	const [note, setNote] = useState(""),
		[tags, setTags] = useState<string[]>([]),
		[month, setMonth] = useState(new Date().toISOString().slice(0, 7)),
		[token, setToken] = useState<string | null>(null),
		[message, setMessage] = useState(""),
		[busy, setBusy] = useState(false),
		[id] = useState(() => crypto.randomUUID()),
		[published, setPublished] = useState(false),
		[challenge, setChallenge] = useState(false),
		[challengeAttempt, setChallengeAttempt] = useState(0);
	const acceptToken = useCallback(
		(value: string | null) => setToken(value),
		[],
	);
	async function send() {
		setBusy(true);
		setMessage("");
		const body = { clientOpId: id, seatKey, note, tags, observedMonth: month };
		try {
			if (!navigator.onLine) {
				await enqueue({
					clientOpId: id,
					endpoint: "/api/tips",
					method: "POST",
					body,
					createdAt: new Date().toISOString(),
				});
				setMessage(
					"연결되면 올려요. 아직 공개되지 않았어요. 내가 공개한 글에서 확인을 마치고 전송할 수 있어요.",
				);
				return;
			}
			await contributionRequest("/api/tips", "POST", body, token ?? undefined);
			completionEvent("tip_published", { entry: "seat", photo: false });
			setPublished(true);
			setMessage("한 줄을 공개했어요. 이 기기 열쇠로 고치거나 지울 수 있어요.");
			onPublished();
		} catch (error) {
			const text =
				error instanceof Error
					? error.message
					: "입력은 그대로 있어요. 다시 시도해 주세요.";
			setMessage(text);
			if (text.includes("사람인지")) {
				setToken(null);
				setChallenge(true);
				setChallengeAttempt((value) => value + 1);
			}
		} finally {
			setBusy(false);
		}
	}
	return (
		<BottomSheet title="이 자리 한 줄 남기기" onClose={onClose}>
			{published ? (
				<div className="stack">
					<p role="status">{message}</p>
					<PhotoAttach reviewId={id} />
					<Link to="/me/posts" onClick={onClose}>
						내가 공개한 글 보기
					</Link>
				</div>
			) : (
				<div className="stack">
					<p>
						직접 앉아본 경험만 남겨요. 연락처·이름·링크는 넣지 마세요. 한 줄은
						서비스와 검색에 공개돼요. 고치기·삭제에는 이 기기 열쇠가 필요해요.
					</p>
					<label className="field">
						한 줄 (10〜300자)
						<textarea
							className="textarea"
							maxLength={300}
							minLength={10}
							value={note}
							onChange={(e) => setNote(e.target.value)}
							rows={4}
						/>
					</label>
					<label className="field">
						관람 연월
						<input
							className="input"
							type="month"
							max={new Date().toISOString().slice(0, 7)}
							value={month}
							onChange={(e) => setMonth(e.target.value)}
						/>
					</label>
					<fieldset>
						<legend>태그는 네 개까지 골라요</legend>
						{TIP_TAGS.map((tag) => (
							<label className="check-item" key={tag}>
								<input
									type="checkbox"
									checked={tags.includes(tag)}
									disabled={!tags.includes(tag) && tags.length >= 4}
									onChange={(e) =>
										setTags(
											e.target.checked
												? [...tags, tag]
												: tags.filter((v) => v !== tag),
										)
									}
								/>
								{tag}
							</label>
						))}
					</fieldset>
					{challenge ? <TurnstileChallenge key={challengeAttempt} onToken={acceptToken} /> : null}
					<button
						type="button"
						className="button button-primary"
						disabled={note.trim().length < 10 || busy || (challenge && !token)}
						onClick={() => void send()}
					>
						{busy ? "올리고 있어요" : "한 줄 공개하기"}
					</button>
					<p role="status">{message}</p>
				</div>
			)}
		</BottomSheet>
	);
}
